type AddonReadyCallback<T> = (api: T) => void;

/**
 * Central registry that lets addons expose typed APIs and discover each
 * other without direct imports or load-order assumptions.
 *
 * - An addon calls `register(name, api)` once it is fully initialized.
 * - Any other addon (or external script) calls `onReady(name, cb)` to
 *   receive the API — immediately if already registered, or as soon as
 *   the target addon registers.
 * - `whenReady(name)` is the promise-based equivalent.
 */
export class AddonRegistry {
  private apis = new Map<string, unknown>();
  private pendingCallbacks = new Map<
    string,
    Array<AddonReadyCallback<unknown>>
  >();
  private readyResolvers = new Map<string, (api: unknown) => void>();
  private readyPromises = new Map<string, Promise<unknown>>();

  private runReadyCallbackSafely<T>(
    name: string,
    callback: AddonReadyCallback<T>,
    api: T,
  ): void {
    try {
      callback(api);
    } catch (error) {
      console.error(`Addon ready callback failed for "${name}".`, error);
    }
  }

  /**
   * Register an addon and expose its public API.
   * All pending `onReady` / `whenReady` waiters are resolved immediately.
   */
  register<T>(name: string, api: T): void {
    this.apis.set(name, api);

    const pending = this.pendingCallbacks.get(name);

    if (pending) {
      for (const callback of pending) {
        this.runReadyCallbackSafely(
          name,
          callback as AddonReadyCallback<T>,
          api,
        );
      }

      this.pendingCallbacks.delete(name);
    }

    const resolve = this.readyResolvers.get(name);

    if (resolve) {
      resolve(api);
      this.readyResolvers.delete(name);
    }
  }

  /**
   * Run `callback` with the addon's API as soon as it is available.
   * If the addon is already registered the callback fires synchronously.
   */
  onReady<T>(name: string, callback: AddonReadyCallback<T>): void {
    const existing = this.apis.get(name);

    if (existing !== undefined) {
      this.runReadyCallbackSafely(name, callback, existing as T);

      return;
    }

    const pending =
      (this.pendingCallbacks.get(name) as
        | Array<AddonReadyCallback<T>>
        | undefined) ?? [];

    pending.push(callback);

    this.pendingCallbacks.set(
      name,
      pending as Array<AddonReadyCallback<unknown>>,
    );
  }

  /**
   * Promise-based variant of `onReady`.
   */
  whenReady<T>(name: string): Promise<T> {
    const existing = this.apis.get(name);

    if (existing !== undefined) {
      return Promise.resolve(existing as T);
    }

    let promise = this.readyPromises.get(name);

    if (!promise) {
      promise = new Promise<unknown>((resolve) => {
        this.readyResolvers.set(name, resolve);
      });

      this.readyPromises.set(name, promise);
    }

    return promise as Promise<T>;
  }

  /**
   * Synchronous, non-blocking lookup. Returns `undefined` when the addon
   * has not registered yet — useful for optional integrations.
   */
  tryGet<T>(name: string): T | undefined {
    return this.apis.get(name) as T | undefined;
  }

  has(name: string): boolean {
    return this.apis.has(name);
  }
}
